export const DOC_TYPES = [
  { value: 'quotation',       label: 'Quotation',       icon: '📋', color: '#2E75B6' },
  { value: 'invoice',         label: 'Invoice',         icon: '🧾', color: '#10B981' },
  { value: 'bill',            label: 'Bill',            icon: '💳', color: '#F59E0B' },
  { value: 'receipt',         label: 'Receipt',         icon: '✅', color: '#8B5CF6' },
  { value: 'purchase_order',  label: 'Purchase Order',  icon: '📦', color: '#EC4899' },
  { value: 'delivery_order',  label: 'Delivery Order',  icon: '🚚', color: '#06B6D4' },
  { value: 'credit_note',     label: 'Credit Note',     icon: '↩️', color: '#EF4444' },
  { value: 'statement',       label: 'Statement',       icon: '📊', color: '#64748B' },
];

export const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft',     color: '#94a3b8' },
  { value: 'sent',      label: 'Sent',      color: '#2E75B6' },
  { value: 'accepted',  label: 'Accepted',  color: '#10B981' },
  { value: 'rejected',  label: 'Rejected',  color: '#EF4444' },
  { value: 'partial',   label: 'Partially paid', color: '#F59E0B' },
  { value: 'paid',      label: 'Paid',      color: '#10B981' },
  { value: 'overdue',   label: 'Overdue',   color: '#EF4444' },
  { value: 'cancelled', label: 'Cancelled', color: '#94a3b8' },
];

// Payments can be recorded against these document types (mirrors server/services/payments.js)
export const PAYABLE_TYPES = ['invoice', 'bill'];
export const PAYMENT_METHODS = ['bank_transfer', 'cash', 'card', 'cheque', 'ewallet', 'online', 'other'];
// What is still owed. Invoices marked paid before payments were tracked have no payment rows.
export const balanceDue = doc =>
  doc?.status === 'paid' ? 0 : Math.max(0, Math.round(((doc?.total || 0) - (doc?.amountPaid || 0)) * 100) / 100);

export const fmtCurrency = (n, sym = 'RM') =>
  `${sym} ${parseFloat(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtDate = d =>
  d ? new Date(d).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

export const getDocType = val => DOC_TYPES.find(t => t.value === val) || DOC_TYPES[0];
export const getStatus  = val => STATUS_OPTIONS.find(s => s.value === val) || STATUS_OPTIONS[0];

// Only delivery orders may hide pricing, so a stale flag can never blank out an
// invoice that was converted from one. Mirrors the same check in server/routes/pdf.js.
export const pricesHidden = doc =>
  doc?.type === 'delivery_order' && doc?.hidePrices === true;

// Fonts offered for the PDF / Word theme. Must stay in sync with FONTS in
// server/utils/theme.js — the server rejects anything not on its own list.
export const PDF_FONTS = [
  'Segoe UI', 'Calibri', 'Arial', 'Helvetica',
  'Georgia', 'Times New Roman', 'Verdana', 'Tahoma',
];

// Visual PDF templates offered in the Settings gallery — ids must stay in sync
// with server/utils/theme.js TEMPLATES.
export const PDF_TEMPLATES = [
  'classic', 'minimal', 'bold', 'elegant', 'gradient', 'slate', 'soft',
  'stripe', 'frame', 'mono', 'sidebar', 'ledger', 'slim', 'curve',
  'diagonal', 'banner', 'duo', 'cards', 'pop', 'royal', 'marker', 'sketch',
  'ghost', 'midnight',
];

// One-click colour pairs for the Settings gallery — each sets themeHeaderColor
// + themeAccentColor and composes with whichever PDF template is selected.
export const COLOR_PRESETS = [
  { id: 'navy',     header: '#1A3C6E', accent: '#2E75B6' }, // the default pair
  { id: 'sky',      header: '#0369A1', accent: '#38BDF8' },
  { id: 'teal',     header: '#0F766E', accent: '#14B8A6' },
  { id: 'emerald',  header: '#14532D', accent: '#10B981' },
  { id: 'burgundy', header: '#7B1E3A', accent: '#C9A227' },
  { id: 'crimson',  header: '#B91C1C', accent: '#F59E0B' },
  { id: 'copper',   header: '#7C2D12', accent: '#EA580C' },
  { id: 'plum',     header: '#4C1D95', accent: '#8B5CF6' },
  { id: 'rose',     header: '#9D174D', accent: '#F472B6' },
  { id: 'graphite', header: '#111827', accent: '#F59E0B' },
];

export const THEME_DEFAULTS = {
  themeTemplate:    'classic',
  themeHeaderColor: '#1A3C6E',
  themeAccentColor: '#2E75B6',
  themeFontFamily:  'Segoe UI',
  themeFontSize:    13,
  themeHeaderSize:  100,
  // Per-textbox header font sizes (px) — must stay in sync with the authored
  // bases in server/utils/theme.js HEADER_BASES.
  themeHeaderNameSize:    26,
  themeHeaderTaglineSize: 11,
  themeHeaderContactSize: 11.5,
  themeHeaderDocTypeSize: 28,
  themeHeaderMetaSize:    12,
  themeHeaderCompact:     false,
  // Space between header and first content block, plus the PREPARED FOR /
  // ISSUED BY sizes — bases mirror server/utils/theme.js.
  themeHeaderGap:         24,
  themePartyLabelSize:    11,
  themePartyNameSize:     14,
  themePartyDetailSize:   12,
};

export const CURRENCIES = [
  // Core
  { code: 'MYR', symbol: 'RM',  label: 'Malaysian Ringgit' },
  { code: 'USD', symbol: '$',   label: 'US Dollar' },
  { code: 'SGD', symbol: 'S$',  label: 'Singapore Dollar' },
  { code: 'EUR', symbol: '€',   label: 'Euro' },
  { code: 'GBP', symbol: '£',   label: 'British Pound' },
  { code: 'CNY', symbol: '¥',   label: 'Chinese Yuan' },

  // Arab League — GCC
  { code: 'AED', symbol: 'AED', label: 'UAE Dirham' },
  { code: 'SAR', symbol: 'SAR', label: 'Saudi Riyal' },
  { code: 'QAR', symbol: 'QAR', label: 'Qatari Riyal' },
  { code: 'KWD', symbol: 'KWD', label: 'Kuwaiti Dinar' },
  { code: 'BHD', symbol: 'BHD', label: 'Bahraini Dinar' },
  { code: 'OMR', symbol: 'OMR', label: 'Omani Rial' },

  // Arab League — Levant
  { code: 'JOD', symbol: 'JOD', label: 'Jordanian Dinar' },
  { code: 'LBP', symbol: 'LBP', label: 'Lebanese Pound' },
  { code: 'SYP', symbol: 'SYP', label: 'Syrian Pound' },
  { code: 'IQD', symbol: 'IQD', label: 'Iraqi Dinar' },

  // Arab League — North Africa
  { code: 'EGP', symbol: 'EGP', label: 'Egyptian Pound' },
  { code: 'LYD', symbol: 'LYD', label: 'Libyan Dinar' },
  { code: 'TND', symbol: 'TND', label: 'Tunisian Dinar' },
  { code: 'DZD', symbol: 'DZD', label: 'Algerian Dinar' },
  { code: 'MAD', symbol: 'MAD', label: 'Moroccan Dirham' },
  { code: 'SDG', symbol: 'SDG', label: 'Sudanese Pound' },
  { code: 'MRU', symbol: 'MRU', label: 'Mauritanian Ouguiya' },

  // Arab League — Horn of Africa
  { code: 'YER', symbol: 'YER', label: 'Yemeni Rial' },
  { code: 'SOS', symbol: 'SOS', label: 'Somali Shilling' },
  { code: 'DJF', symbol: 'DJF', label: 'Djiboutian Franc' },
  { code: 'KMF', symbol: 'KMF', label: 'Comorian Franc' },
];

// Subscription list prices per currency — used until /stripe/config answers with
// the live amounts read from Stripe (keep in sync with server/routes/stripe.js).
export const LIST_PLANS = {
  defaultCurrency: 'myr',
  plans: { myr: { monthly: 40, yearly: 400 }, usd: { monthly: 10, yearly: 100 } },
};
const CURRENCY_PREFIX = { myr: 'RM', usd: '$' };
const MALAYSIA_TIMEZONES = ['Asia/Kuala_Lumpur', 'Asia/Kuching'];
const BILLING_CURRENCY_KEY = 'billingCurrency';

// Currency to bill in: the visitor's saved choice, else RM inside Malaysia and USD elsewhere.
export const billingCurrency = cfg => {
  const { plans, defaultCurrency } = cfg?.plans ? cfg : LIST_PLANS;
  let saved = null;
  try { saved = localStorage.getItem(BILLING_CURRENCY_KEY); } catch { /* storage blocked */ }
  if (plans[saved]) return saved;
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* very old browser */ }
  const guess = MALAYSIA_TIMEZONES.includes(tz) ? 'myr' : 'usd';
  return plans[guess] ? guess : defaultCurrency;
};

export const saveBillingCurrency = currency => {
  try { localStorage.setItem(BILLING_CURRENCY_KEY, currency); } catch { /* storage blocked */ }
};

// Display strings for the plan cards (Landing + Subscription).
export const planPricing = (cfg, currency) => {
  const { plans, defaultCurrency } = cfg?.plans ? cfg : LIST_PLANS;
  const cur = plans[currency] ? currency : defaultCurrency;
  const { monthly, yearly } = plans[cur];
  const prefix = CURRENCY_PREFIX[cur] || `${cur.toUpperCase()} `;
  const fmt = n => `${prefix}${Number.isInteger(n) ? n : n.toFixed(2)}`;
  return {
    currency: cur,
    currencies: Object.keys(plans),
    monthly: fmt(monthly),
    yearly:  fmt(yearly),
    yearlyPerMonth: fmt(Math.round((yearly / 12) * 100) / 100),
    savePct: Math.round((1 - yearly / (monthly * 12)) * 100),
  };
};

// Digits-only number for a wa.me link. Local Malaysian numbers (012-345 6789) get
// the country code; anything already international is left as typed.
export const whatsappNumber = (phone, currency) => {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0') && (!currency || currency === 'MYR')) digits = `6${digits}`;
  return digits;
};
